<script lang="ts">
  import { AlertTriangle } from '@lucide/svelte';
  interface Props {
    filteredProducts: any[];
  }

  let { filteredProducts }: Props = $props();

  const insights = $derived.by(() => {
    const products = filteredProducts || [];
    const totalCount = products.length;
    const lowStock = products.filter(p => p.stock < (p.lowStockThreshold ?? 10)).length;

    let active = 0, draft = 0, archived = 0, totalValuation = 0, totalOfferSum = 0;
    products.forEach(p => {
      if (p.status === 'ACTIVE') active++;
      else if (p.status === 'DRAFT') draft++;
      else archived++;
      totalValuation += Number(p.offerPrice) * p.stock;
      totalOfferSum += Number(p.offerPrice);
    });

    return {
      totalCount, lowStock, active, draft, archived, totalValuation,
      avgPrice: totalCount > 0 ? totalOfferSum / totalCount : 0
    };
  });
</script>

<div class="space-y-6">
  <!-- KPI Cards -->
  <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
    <div class="bg-base-100 p-5 border border-base-300 rounded-2xl shadow-xl space-y-1">
      <span class="text-xs font-bold text-base-content/50 uppercase tracking-wider">Total Value</span>
      <div class="text-2xl font-black text-base-content"><span class="text-lg opacity-75 font-semibold">Rs.</span> {insights.totalValuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      <p class="text-[10px] text-base-content/40">Combined value of all products in stock</p>
    </div>
    <div class="bg-base-100 p-5 border border-base-300 rounded-2xl shadow-xl space-y-1">
      <span class="text-xs font-bold text-base-content/50 uppercase tracking-wider">Total Products</span>
      <div class="text-2xl font-black text-primary">{insights.totalCount} Products</div>
      <p class="text-[10px] text-base-content/40">Total unique items in the catalog database</p>
    </div>
    <div class="bg-base-100 p-5 border border-base-300 rounded-2xl shadow-xl space-y-1">
      <span class="text-xs font-bold text-base-content/50 uppercase tracking-wider">Low Stock Warnings</span>
      <div class="text-2xl font-black text-error">{insights.lowStock} Items</div>
      <p class="text-[10px] text-base-content/40">Products with stock levels below 10 units</p>
    </div>
    <div class="bg-base-100 p-5 border border-base-300 rounded-2xl shadow-xl space-y-1">
      <span class="text-xs font-bold text-base-content/50 uppercase tracking-wider">Average Price</span>
      <div class="text-2xl font-black text-accent"><span class="text-lg opacity-75 font-semibold">Rs.</span> {insights.avgPrice.toFixed(2)}</div>
      <p class="text-[10px] text-base-content/40">Average price of catalog products</p>
    </div>
  </div>

  <!-- Charts & Lists -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <!-- Status Breakdown -->
    <div class="bg-base-100 p-6 border border-base-300 rounded-2xl shadow-xl space-y-5">
      <div class="space-y-1">
        <h4 class="font-bold text-base-content uppercase tracking-wider text-sm">Status Breakdown</h4>
        <p class="text-xs text-base-content/50">Distribution of product status across the database</p>
      </div>
      <div class="space-y-4">
        <div class="w-full bg-base-200 rounded-full h-8 overflow-hidden flex border border-base-300 p-0.5">
          {#if insights.totalCount > 0}
            <div style="width: {(insights.active / insights.totalCount) * 100}%" class="bg-success h-full first:rounded-l-full last:rounded-r-full transition-all duration-500" title="Active"></div>
            <div style="width: {(insights.draft / insights.totalCount) * 100}%" class="bg-warning h-full first:rounded-l-full last:rounded-r-full transition-all duration-500" title="Draft"></div>
            <div style="width: {(insights.archived / insights.totalCount) * 100}%" class="bg-neutral h-full first:rounded-l-full last:rounded-r-full transition-all duration-500" title="Archived"></div>
          {:else}
            <div class="w-full bg-base-300 h-full rounded-full"></div>
          {/if}
        </div>
        <div class="grid grid-cols-3 gap-2 pt-2 text-center text-xs font-bold">
          <div class="p-3 bg-success/5 border border-success/20 rounded-xl">
            <span class="text-success uppercase tracking-wider block text-[10px]">Active</span>
            <span class="text-lg text-base-content font-bold">{insights.active}</span>
            <span class="text-[10px] text-base-content/60 block">({insights.totalCount > 0 ? Math.round((insights.active / insights.totalCount) * 100) : 0}%)</span>
          </div>
          <div class="p-3 bg-warning/5 border border-warning/20 rounded-xl">
            <span class="text-warning uppercase tracking-wider block text-[10px]">Draft</span>
            <span class="text-lg text-base-content font-bold">{insights.draft}</span>
            <span class="text-[10px] text-base-content/60 block">({insights.totalCount > 0 ? Math.round((insights.draft / insights.totalCount) * 100) : 0}%)</span>
          </div>
          <div class="p-3 bg-base-200 border border-base-300 rounded-xl">
            <span class="text-base-content/60 uppercase tracking-wider block text-[10px]">Archived</span>
            <span class="text-lg text-base-content font-bold">{insights.archived}</span>
            <span class="text-[10px] text-base-content/60 block">({insights.totalCount > 0 ? Math.round((insights.archived / insights.totalCount) * 100) : 0}%)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Low Stock List -->
    <div class="bg-base-100 p-6 border border-base-300 rounded-2xl shadow-xl space-y-4">
      <div class="space-y-1">
        <h4 class="font-bold text-base-content uppercase tracking-wider text-sm">Low Stock Items</h4>
        <p class="text-xs text-base-content/50">Items with stock levels below warning thresholds</p>
      </div>
      <div class="space-y-2 max-h-56 overflow-y-auto pr-1">
        {#each (filteredProducts.filter(p => p.stock < (p.lowStockThreshold ?? 10)).sort((a,b) => a.stock - b.stock).slice(0, 5)) as prod}
          <div class="flex items-center justify-between p-3 bg-base-200 border border-base-300 rounded-xl text-xs hover:border-error/20 transition">
            <div class="flex items-center gap-2.5 min-w-0">
              <div class="w-8 h-8 rounded bg-error/10 text-error flex items-center justify-center font-bold">
                <AlertTriangle class="w-4.5 h-4.5" />
              </div>
              <div class="min-w-0">
                <div class="font-bold text-base-content truncate max-w-[180px]">{prod.name}</div>
                <div class="text-[10px] font-mono text-base-content/50">{prod.sku}</div>
              </div>
            </div>
            <div class="text-right">
              <span class="badge badge-error font-bold text-[10px]">{prod.stock} units left</span>
            </div>
          </div>
        {:else}
          <div class="py-10 text-center text-xs font-semibold text-base-content/40">
            No products are low on stock.
          </div>
        {/each}
      </div>
    </div>
  </div>
</div>
